import { Car, Clock, Mail, MapPin, Navigation, Phone } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { SITE } from "../../lib/site";

const Contact = () => (
    <section
        id="contact"
        data-testid="contact-section"
        className="relative bg-emerald-900/25 py-28 md:py-36"
    >
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col justify-center">
                <Reveal>
                    <Overline>Contact</Overline>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-cream sm:text-5xl">
                        Visit{" "}
                        <span className="italic text-gold-light">
                            Flammingo Luxurious Spa
                        </span>
                    </h2>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-12 space-y-9">
                        <div className="flex items-start gap-5">
                            <MapPin
                                size={20}
                                strokeWidth={1.5}
                                className="mt-1 shrink-0 text-gold"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold/80">
                                    Location
                                </p>
                                <p
                                    data-testid="contact-address"
                                    className="mt-2 text-base font-light leading-relaxed tracking-wide text-cream"
                                >
                                    {SITE.name}
                                    <br />
                                    {SITE.addressLine1}
                                    <br />
                                    {SITE.addressLine2}
                                    <br />
                                    {SITE.addressLine3}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-5">
                            <Phone
                                size={20}
                                strokeWidth={1.5}
                                className="mt-1 shrink-0 text-gold"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold/80">
                                    Phone
                                </p>
                                <a
                                    href={SITE.phoneHref}
                                    data-testid="contact-phone-link"
                                    className="mt-2 block text-base font-light tracking-wide text-cream transition-colors duration-300 hover:text-gold"
                                >
                                    {SITE.phoneDisplay}
                                </a>
                                <a
                                    href={SITE.phone2Href}
                                    data-testid="contact-phone2-link"
                                    className="mt-1.5 block text-base font-light tracking-wide text-cream transition-colors duration-300 hover:text-gold"
                                >
                                    {SITE.phone2Display}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-start gap-5">
                            <Mail
                                size={20}
                                strokeWidth={1.5}
                                className="mt-1 shrink-0 text-gold"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold/80">
                                    Email
                                </p>
                                <a
                                    href={SITE.emailHref}
                                    data-testid="contact-email-link"
                                    className="mt-2 block break-all text-base font-light tracking-wide text-cream transition-colors duration-300 hover:text-gold"
                                >
                                    {SITE.email}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-start gap-5">
                            <Clock
                                size={20}
                                strokeWidth={1.5}
                                className="mt-1 shrink-0 text-gold"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold/80">
                                    Opening Hours
                                </p>
                                <p
                                    data-testid="contact-hours"
                                    className="mt-2 text-base font-light tracking-wide text-cream"
                                >
                                    {SITE.hours}
                                    <span className="mt-1 block text-sm text-sage/80">
                                        {SITE.days}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.3}>
                    <div className="mt-12 flex flex-wrap gap-5">
                        <a
                            href={SITE.phoneHref}
                            data-testid="contact-call-button"
                            className="inline-flex items-center gap-3 bg-gold px-8 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-emerald-950 transition-colors duration-300 hover:bg-gold-light"
                        >
                            <Phone size={15} aria-hidden="true" />
                            Call Now
                        </a>
                        <a
                            href={SITE.emailHref}
                            data-testid="contact-email-button"
                            className="inline-flex items-center gap-3 border border-gold/40 px-8 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                        >
                            <Mail size={15} aria-hidden="true" />
                            Email Us
                        </a>
                        <a
                            href={SITE.directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-directions-button"
                            className="inline-flex items-center gap-3 border border-cream/25 px-8 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            <Navigation size={15} aria-hidden="true" />
                            Get Directions
                        </a>
                    </div>
                    <p
                        data-testid="contact-parking-note"
                        className="mt-7 flex items-center gap-2.5 text-[11px] font-light uppercase tracking-[0.25em] text-sage/70"
                    >
                        <Car size={15} className="text-gold/70" aria-hidden="true" />
                        Mall parking available at Palm Beach Galleria
                    </p>
                </Reveal>
            </div>

            <Reveal delay={0.2} className="flex">
                <div className="relative w-full border border-gold/25 p-2">
                    <iframe
                        title="Map of Vashi, Navi Mumbai — Flammingo Luxurious Spa"
                        data-testid="contact-map"
                        src="https://maps.google.com/maps?q=Palm%20Beach%20Galleria%20Mall%2C%20Sector%2019D%2C%20Vashi%2C%20Navi%20Mumbai&t=&z=16&ie=UTF8&iwloc=&output=embed"
                        className="map-dark h-full min-h-[380px] w-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    />
                </div>
            </Reveal>
        </div>
    </section>
);

export default Contact;
